import safeJsonParse from '@/utils/safeJsonParse';
import React, { useEffect, useState } from 'react';

/**
 * Function to get parsed JSON value from sessionStorage.
 * @param key
 * @returns
 */
const getItem = <T>(key: string): T | undefined | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return safeJsonParse(window.sessionStorage.getItem(key) ?? '');
};

/**
 * Function to store a stringified JSON value against a key in sessionStorage.
 * @param key
 * @param value
 * @returns
 */
const setItem = <T>(key: string, value: T) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * Function to remove the item from the session storage
 * @param key
 * @returns
 */
const removeItem = (key: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(key);
};

/**
 * Function to initialize sessionStorage item for a given key.
 * @param key
 * @param initialValue
 * @returns
 */
const initItem = <T>(key: string, initialValue?: T): T | null => {
  const sessionValue = getItem<T>(key);
  if (typeof sessionValue !== 'undefined') {
    return sessionValue;
  }
  setItem(key, initialValue);
  return initialValue ?? null;
};
/**
 * Hook to access session storage and setting it in state
 * @param key
 * @param initialValue
 * @returns [state, stateSetter, removeItemFromStorage]
 */
const useSessionStorage = <T>(
  key: string,
  initialValue?: T
): [state: T | null, setter: React.Dispatch<React.SetStateAction<T | null>>, removeItem: () => void] => {
  const [state, setState] = useState<T | null>(() => initItem(key, initialValue));

  // re-initialize storage item on key change (for dynamic keys)
  useEffect(() => {
    const sessionValue = initItem<T>(key, initialValue);
    setItem(key, sessionValue);
    setState(sessionValue);
  }, [key, initialValue]);

  if (typeof window === 'undefined') {
    return [state, function () {}, function () {}];
  }

  const setter: React.Dispatch<React.SetStateAction<T | null>> = React.useCallback((value) => {
    setState((prevState) => {
      const newValue = value instanceof Function ? value(prevState) : value;
      setItem<T>(key, newValue as T);
      return newValue;
    });
  }, [key]);

  const remove = React.useCallback(() => {
    setState(null);
    removeItem(key);
  }, [key]);

  return [
    state,
    setter,
    remove,
  ];
};

export default useSessionStorage;